import {
    Form,
    FormField,
    FormSection,
    FormType,
    InputType,
    FilteredValue,
    ReactiveFormChangedField,
    ReactiveFormValue,
    ReactiveDropdownDataSource,
} from '@fitogest/reactive-form-builder/interfaces';
import { ClientesEnvironmentDefinition } from '../environment-variables';
import { ClientesListsDefinition } from './list-variables';
import { StorageManager } from '../../../../common/storage-manager.class';
import { environment } from '../../../../../environments/environment';
import { UserModel } from '../../../../models/usuarios/user.model';
import { ClienteModel } from '../../../../models/cliente.model';
import {
  FIRST_CHAR_UNION,
  list,
  ReactiveDomainSectionFactory,
  ReactiveObservacionesFieldFactory,
  ReactiveTextareaFieldFactory,
  ReactiveTextFieldFactory,
} from '@fitogest/reactive-form-builder/utils';

export interface ClientesDropdownEndpoints {
  readonly usuarios?: unknown;
}

export class ClientesFormDefinition {
  public formType!: FormType;

  public env: ClientesEnvironmentDefinition = new ClientesEnvironmentDefinition();

  public lists: ClientesListsDefinition = new ClientesListsDefinition();

  public usuario = StorageManager.getUser();

  public model: ReactiveFormValue<ClienteModel> = {} as ReactiveFormValue<ClienteModel>;

  public tag: ReactiveFormChangedField<ClienteModel> = '';

  public formFieldsSection!: FormSection<object>;

  public formFieldsPrevilsa1!: FormSection<object>;

  public formFieldsPrevilsa2!: FormSection<object>;

  public formFieldsEstadosReglas!: FormSection<object>;

  public formFields!: Form<ClienteModel>;

  public fieldNames: FormField<any>[] = [];

  public fieldLabels: string[] = [];

  public spanishIdKeyPattern: RegExp = FIRST_CHAR_UNION;

  private readonly dropdownEndpoints?: ClientesDropdownEndpoints;

  constructor(
    type: FormType,
    _model: Record<string,string>,
    table: (string[] | string[][])[] | null,
    dropdownEndpoints?: ClientesDropdownEndpoints
  ) {
    this.formType = +type;
    if (dropdownEndpoints !== undefined) {
      this.dropdownEndpoints = dropdownEndpoints;
    }

    const action = this.formType === FormType.CREATION ? 'Crear' : 'Actualizar';

    this.formFields = {
      name: 'clientes',
      header: action + ' ' + this.env.clienteName,
      type: this.formType,
      sections: this.getFormSections(),
      table: { title: '', showIndex: false, data: table?.[0], style: table?.[1]},
      uploadButton: {isEnabled: false, label: ''},
      signed: this.env.showClientesFirma
    };
  }


  private createUsuariosDataSourceField(): Partial<Pick<FormField<ClienteModel>, 'dataSource'>> {
    const endpoint = this.dropdownEndpoints?.usuarios;

    if (endpoint === undefined || endpoint === null) {
      return {};
    }

    const dataSource: ReactiveDropdownDataSource<ClienteModel> = {
      endpoint,
      cacheKey: 'clientes-usuarios',
      distinctBy: 'id',
      cacheEmptyResults: false,
      mapOption: (item) => {
        const user = item as Partial<UserModel>;
        const label = `${user.nombre ?? ''} ${user.apellidos ?? ''}`.trim() || String(user.id ?? '');

        return {
          label,
          value: item as UserModel,
          raw: item,
        };
      },
    };

    return { dataSource };
  }


  public getFormFields(): FormField<any>[] {
    const standardCodeLength = 45;

    const prosanzcuCodeLength = 3;

    const formFields: FormField<any>[] = [
      ReactiveTextFieldFactory.create<ClienteModel>({
        name: 'nombre',
        label: 'Nombre o Razón Social',
        order: 1,
        conditions: { rememberedOnDuplicate: true, required: true },
      }),
      ReactiveTextFieldFactory.create<ClienteModel>({
        name: 'apellido_1',
        label: 'Primer Apellido',
        order: 2,
        conditions: { rememberedOnDuplicate: true },
      }),
      ReactiveTextFieldFactory.create<ClienteModel>({
        name: 'apellido_2',
        label: 'Segundo Apellido',
        order: 3,
        conditions: { rememberedOnDuplicate: true },
      }),
      {
        name: 'nif',
        label: 'NIF / CIF',
        input: {
          isEditable: true,
          type: InputType.TEXT,
          maxLength: 9,
          textConfiguration: {
            regex: this.spanishIdKeyPattern
          }
        },
        updateValue: (it: any): void => {
          const input = it.input;
          const textCfg = (input.textConfiguration ??= {});

          textCfg.regex = this.spanishIdKeyPattern;
        },
        checkForDuplicates: (nif: string) => this.env.appName !== 'innovia' ? (this.lists.clientes.values ?? []).some(opt => {
          const row = (opt?.value as ClienteModel) ?? undefined;

          if (this.model.id != null && row?.id.toString() === this.model.id.toString()) {return false;}

          return row?.nif === nif;
        }) : false,
        width: 3,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: this.env.activateREA || this.env.appName === 'innovia',
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: true
        },
        order: 4,
      },
      {
        name: 'sector',
        label: 'Sector',
        input: {isEditable: true, type: InputType.TEXT},
        width: 4,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: this.env.appName === 'crm'
        },
        order: 5,
      },
      {
        name: 'cooperativa',
        label: 'Cooperativa',
        input: {isEditable: true, type: InputType.TEXT},
        width: 3,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: true,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: this.env.showClientesCooperativa
        },
        order: 6
      },
      {
        name: 'codigo',
        label: this.env.appName === 'prodalbar' ? 'Nº Socio' :'Código',
        input: {
          isEditable: true,
          maxLength: this.env.appName === 'prodalbar' ? prosanzcuCodeLength : standardCodeLength,
          type: InputType.TEXT
        },
        width: 3,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: this.env.hasCodigoCliente
        },
        order: 7
      },
      {
        name: 'alias',
        label: this.env.appName === 'covidai' ? 'Almacén' : 'Alias',
        input: {
          isEditable: true,
          data: this.lists.almacenesCovidai,
          type: InputType.DROPDOWN
        },
        width: 3,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: this.env.hasClientesAliasField
        },
        order: 8
      },
      {
        name: 'direccion',
        label: 'Dirección',
        input: {isEditable: true, type: InputType.TEXT},
        width: 3,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: true
        },
        order: 9
      },
      {
        name: 'localidad',
        label: 'Localidad',
        input: {isEditable: true, type: InputType.TEXT},
        width: 3,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: true
        },
        order: 10
      },
      {
        name: 'codigo_postal',
        label: 'Codigo Postal',
        input: {isEditable: true, type: InputType.NUMERIC},
        width: 3,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: true
        },
        order: 11
      },
      {
        name: 'provincia',
        label: 'Provincia',
        input: {isEditable: true, type: InputType.TEXT},
        width: 3,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: true
        },
        order: 12
      },
      {
        name: 'municipio_ine',
        label: 'Código Municipio INE',
        input: {isEditable: true, type: InputType.TEXT},
        width: 3,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: this.env.showReto
        },
        order: 13
      },
      {
        name: 'ropo',
        label: 'Nº ROPO SU',
        input: {isEditable: true, type: InputType.TEXT},
        width: 3,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: this.env.showReto
        },
        order: 14
      },
      {
        name: 'telefono',
        label: 'Teléfono',
        input: {isEditable: true, type: InputType.NUMERIC},
        width: 3,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: true
        },
        order: 15
      },
      {
        name: 'fax',
        label: 'Fax',
        input: {isEditable: true, type: InputType.TEXT},
        width: 3,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: this.env.showReto
        },
        order: 16
      },
      {
        name: 'email',
        label: 'Email',
        input: {
          isEditable: true,
          type: InputType.TEXT,
        },
        width: 3,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: true
        },
        order: 17
      },
      {
        name: 'gerente',
        label: 'Gerente',
        input: {isEditable: true, type: InputType.TEXT},
        width: 3,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: this.env.showCrm
        },
        order: 18
      },
      {
        name: 'email_gerente',
        label: 'Email del Gerente',
        input: {isEditable: true, type: InputType.TEXT},
        width: 3,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: this.env.showCrm
        },
        order: 19
      },
      {
        name: 'telefono_gerente',
        label: 'Teléfono del Gerente',
        input: {isEditable: true, type: InputType.TEXT},
        width: 3,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: this.env.showCrm
        },
        order: 20
      },
      {
        name: 'responsable_calidad',
        label: 'Responsable de Calidad',
        input: {isEditable: true, type: InputType.TEXT},
        width: 3,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: this.env.showCrm
        },
        order: 21
      },
      {
        name: 'email_responsable_calidad',
        label: 'Email del Responsable de Calidad',
        input: {isEditable: true, type: InputType.TEXT},
        width: 3,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: this.env.showCrm
        },
        order: 22
      },
      {
        name: 'telefono_responsable_calidad',
        label: 'Teléfono del Responsable de Calidad',
        input: {isEditable: true, type: InputType.TEXT},
        width: 3,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: this.env.showCrm
        },
        order: 23
      },
      {
        name:
          (this.formType !== FormType.INFORMATIVE && this.formType !== FormType.EXPORT ) ?
            'id_usuario_asignado' :
            'usuario_asignado_nombre',
        label: this.env.appName === 'crm' ? 'Comercial Asignado' : 'Técnico/Aplicador Asignado',
        input: {
          isEditable: true,
          data: this.lists.usuarios,
          defaultValue: StorageManager.getUser().id,
          type: this.env.appName === 'agrosalvi' ||
                        environment.features.showMultiselectAplicadores ?
            InputType.MULTISELECT :
            InputType.DROPDOWN,
          dropdownConfiguration: {
            valuePrimaryKey: 'id',
            filter: (usuario: FilteredValue<UserModel>): boolean => {
              if (this.env.applicationType === 'cropgest') {
                return usuario.value?.rol === 'responsable';
              } else {
                return true;
              }// if();
            },
          }
        },
        ...this.createUsuariosDataSourceField(),
        width: 3,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: this.env.showTecnicoAsignado
        },
        order: 23
      },
      {
        name: 'operador_explotacion',
        label: 'Nº Operador explotación',
        input: {isEditable: true, type: InputType.TEXT},
        width: 3,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: this.env.appName === 'pyf'
        },
        order: 24
      },
      {
        name: 'regepa',
        label: 'REGEPA',
        input: {isEditable: true, type: InputType.TEXT},
        width: 3,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: this.env.showClientesRegepa
        },
        order: 25
      },
      ReactiveObservacionesFieldFactory.create<ClienteModel>({
        order: 26,
        conditions: { rememberedOnDuplicate: true, visible: this.env.showClientesObservaciones },
      }),
      {
        name: 'tipo',
        label: 'Tipo',
        input: {
          isEditable: true,
          data: this.env.showCrm ?  list(['...', null], 'Afianzado', 'Potencial') : this.lists.tipo,
          type: InputType.DROPDOWN
        },
        width: 3,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: this.env.appName === 'agrosalvi' || this.env.showCrm,
        },
        order: 27
      },
      ReactiveTextareaFieldFactory.create<ClienteModel>({
        name: 'notas',
        label: 'Notas',
        order: 28,
        conditions: { rememberedOnDuplicate: true, visible: this.env.showClientesNotas },
      }),
      {
        name: 'acceso_plataforma',
        label: 'Acceso a Plataforma',
        input: {isEditable: true, type: InputType.SWITCH},
        width: 3,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: this.env.applicationType === 'cropgest' && StorageManager.getUser().rol === 'admin',
        },
        order: 29
      }
    ];

    this.fieldNames = this.fieldNames.concat(formFields.filter((it) => it.conditions.isVisible));
    this.fieldLabels = this.fieldLabels.concat(formFields.filter((it) => it.conditions.isVisible).map(it => it.label));
    return formFields.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }

  public getPrevilsa1Fields(isSectionVisible: boolean): FormField<any>[] {
    const previlsa1Fields: FormField<any>[] = [
      {
        name: 'persona1',
        label: 'Persona de contacto 1',
        input: {isEditable: true, type: InputType.TEXT},
        width: 3,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: isSectionVisible,
        },
        order: 1
      },
      {
        name: 'persona2',
        label: 'Persona de contacto 2',
        input: {isEditable: true, type: InputType.TEXT},
        width: 3,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: isSectionVisible,
        },
        order: 2
      },
      {
        name: 'telefono1',
        label: 'Teléfono 1',
        input: {isEditable: true, type: InputType.TEXT},
        width: 3,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: isSectionVisible,
        },
        order: 3
      },
      {
        name: 'telefono2',
        label: 'Teléfono 2',
        input: {isEditable: true, type: InputType.TEXT},
        width: 3,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: isSectionVisible,
        },
        order: 4
      },
      {
        name: 'cargo1',
        label: 'Cargo 1',
        input: {isEditable: true, type: InputType.TEXT},
        width: 3,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: isSectionVisible,
        },
        order: 5
      },
      {
        name: 'cargo2',
        label: 'Cargo 2',
        input: {isEditable: true, type: InputType.TEXT},
        width: 3,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: isSectionVisible,
        },
        order: 6
      },
    ];

    this.fieldNames = this.fieldNames.concat(previlsa1Fields.filter((it) => it.conditions.isVisible));
    this.fieldLabels = this.fieldLabels.concat(previlsa1Fields.filter((it) => it.conditions.isVisible).map(it => it.label));
    return previlsa1Fields.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }

  public getPrevilsa2Fields(isSectionVisible: boolean): FormField<any>[] {
    const previlsa2Fields: FormField<any>[] = [
      {
        name: 'grupo_compra',
        label: 'Grupo de compra',
        input: {
          isEditable: true,
          data: this.lists.gruposCompra,
          type: InputType.DROPDOWN
        },
        width: 3,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: isSectionVisible,
        },
        order: 1
      },
      {
        name: 'tipo_cliente',
        label: 'Tipo de cliente',
        input: {
          isEditable: true,
          data: this.lists.tiposCliente,
          type: InputType.DROPDOWN
        },
        width: 3,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: isSectionVisible,
        },
        order: 2
      },
      {
        name: 'clase_cliente',
        label: 'Clase de cliente',
        input: {
          isEditable: true,
          data: this.lists.clasesCliente,
          type: InputType.DROPDOWN
        },
        width: 3,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: isSectionVisible,
        },
        order: 3
      },
      {
        name: 'condiciones_venta',
        label: 'Condiciones de venta',
        input: {isEditable: true, type: InputType.TEXT},
        width: 3,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: isSectionVisible,
        },
        order: 4
      },
      {
        name: 'notas',
        label: 'Notas',
        input: {isEditable: true, type: InputType.TEXTAREA},
        width: 3,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: isSectionVisible,
        },
        order: 5
      },
      {
        name: 'comercial',
        label: 'Comercial',
        input: {isEditable: true, type: InputType.TEXT},
        width: 3,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: isSectionVisible,
        },
        order: 6
      },
    ];

    this.fieldNames = this.fieldNames.concat(previlsa2Fields.filter((it) => it.conditions.isVisible));
    this.fieldLabels = this.fieldLabels.concat(previlsa2Fields.filter((it) => it.conditions.isVisible).map(it => it.label));
    return previlsa2Fields.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }

  public getEstadosReglasFields(isSectionVisible: boolean): FormField<any>[] {
    const estadosReglasFields: FormField<any>[] = [
      {
        name: 'estado',
        label: 'Estado',
        input: {
          isEditable: true,
          data: this.lists.tiposEstado,
          type: InputType.DROPDOWN
        },
        width: 3,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: isSectionVisible,
        },
        order: 1
      },
      {
        name: 'regla_negocio',
        label: 'Reglas de negocio',
        input: {
          isEditable: true,
          data: this.lists.tiposReglaNecogio,
          type: InputType.DROPDOWN
        },
        width: 3,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: isSectionVisible,
        },
        order: 2
      },
    ];

    this.fieldNames = this.fieldNames.concat(estadosReglasFields.filter((it) => it.conditions.isVisible));
    this.fieldLabels = this.fieldLabels.concat(estadosReglasFields.filter((it) => it.conditions.isVisible).map(it => it.label));
    return estadosReglasFields.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }

  public getSDIFields(isSectionVisible: boolean): FormField<any>[] {
    const sdiFields: FormField<any>[] = [
      {
        name: 'nomcli',
        label: 'Nombre',
        input: {isEditable: false, type: InputType.TEXT},
        width: 3,
        conditions: {
          isRememberedOnDuplicate: false,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: isSectionVisible,
        },
        order: 1
      },
      {
        name: 'razon',
        label: 'Razón',
        input: {isEditable: false, type: InputType.TEXT},
        width: 3,
        conditions: {
          isRememberedOnDuplicate: false,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: isSectionVisible,
        },
        order: 2
      },
      {
        name: 'nifcli',
        label: 'NIF',
        input: {isEditable: false, type: InputType.TEXT},
        width: 3,
        conditions: {
          isRememberedOnDuplicate: false,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: isSectionVisible,
        },
        order: 3
      },
      {
        name: 'dircli',
        label: 'Dirección',
        input: {isEditable: false, type: InputType.TEXT},
        width: 3,
        conditions: {
          isRememberedOnDuplicate: false,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: isSectionVisible,
        },
        order: 4
      },
      {
        name: 'dtocli',
        label: 'Dto.',
        input: {isEditable: false, type: InputType.TEXT},
        width: 3,
        conditions: {
          isRememberedOnDuplicate: false,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: isSectionVisible,
        },
        order: 5
      },
      {
        name: 'nomprovi',
        label: 'Provincia',
        input: {isEditable: false, type: InputType.TEXT},
        width: 3,
        conditions: {
          isRememberedOnDuplicate: false,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: isSectionVisible,
        },
        order: 6
      },
      {
        name: 'pobcli',
        label: 'Población',
        input: {isEditable: false, type: InputType.TEXT},
        width: 3,
        conditions: {
          isRememberedOnDuplicate: false,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: isSectionVisible,
        },
        order: 7
      },
      {
        name: 'telcli',
        label: 'Teléfono',
        input: {isEditable: false, type: InputType.TEXT},
        width: 3,
        conditions: {
          isRememberedOnDuplicate: false,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: isSectionVisible,
        },
        order: 8
      },
      {
        name: 'email',
        label: 'Email',
        input: {isEditable: false, type: InputType.TEXT},
        width: 3,
        conditions: {
          isRememberedOnDuplicate: false,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: isSectionVisible,
        },
        order: 9
      }
    ];

    this.fieldNames = this.fieldNames.concat(sdiFields.filter((it) => it.conditions.isVisible));
    this.fieldLabels = this.fieldLabels.concat(sdiFields.filter((it) => it.conditions.isVisible).map(it => it.label));
    return sdiFields.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }

  public getHiddenFields(): FormField<any>[] {
    const hiddenFields: FormField<any>[] = [
      {
        name: 'id',
        label: '',
        input: {
          type: InputType.HIDDEN,
          defaultValue: this.model.id,
          isEditable: false
        },
        width: 2,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: true,
        },
        order: 1
      },
      {
        name: 'id_usuario',
        label: '',
        input: {
          isEditable: false,
          defaultValue: StorageManager.getUser().id,
          type: InputType.HIDDEN,
        },
        updateValue: () => this.model.id_usuario = this.model.id_usuario_asignado ?? this.model.id_usuario ?? '',
        width: 3,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: true
        },
        order: 1
      },
      {
        name: 'id_usuario_asignado2',
        label: '',
        input: {
          isEditable: false,
          defaultValue: StorageManager.getUser().id,
          type: InputType.HIDDEN,
        },
        width: 3,
        conditions: {
          isRememberedOnDuplicate: true,
          isRequired: false,
          isRetained: false,
          hasUndoEnabled: false,
          isVisible: this.env.showTecnicoAsignado
        },
        order: 2
      }
    ];

    this.fieldNames = this.fieldNames.concat(hiddenFields.filter((it) => it.conditions.isVisible));
    this.fieldLabels = this.fieldLabels.concat(hiddenFields.filter((it) => it.conditions.isVisible).map(it => it.label));
    return hiddenFields.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }


  private getFormSections(): FormSection<any>[] {
    const isPrevilsaSectionVisible = this.env.appName === 'previlsa';

    const sections: FormSection<ClienteModel>[] = [
      ReactiveDomainSectionFactory.identificacion<ClienteModel>({
        name: '',
        fields: this.getFormFields(),
        order: 1,
      }),
      ReactiveDomainSectionFactory.datosAdministrativos<ClienteModel>({
        name: '',
        visible: isPrevilsaSectionVisible,
        fields: this.getPrevilsa1Fields(isPrevilsaSectionVisible),
        order: 2,
      }),
      ReactiveDomainSectionFactory.datosAdministrativos<ClienteModel>({
        name: '',
        visible: isPrevilsaSectionVisible,
        fields: this.getPrevilsa2Fields(isPrevilsaSectionVisible),
        order: 3,
      }),
      ReactiveDomainSectionFactory.datosAdministrativos<ClienteModel>({
        name: '',
        visible: this.env.showFieldsClientesEstadoReglas,
        fields: this.getEstadosReglasFields(this.env.showFieldsClientesEstadoReglas),
        order: 4,
      }),
      ReactiveDomainSectionFactory.datosAdministrativos<ClienteModel>({
        name: '',
        visible: this.env.showSdiClientes,
        fields: this.getSDIFields(this.env.showSdiClientes),
        order: 4,
      }),
      ReactiveDomainSectionFactory.datosAdministrativos<ClienteModel>({
        name: '',
        fields: this.getHiddenFields(),
        order: 6,
      }),
    ];

    return sections.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }
}